import { defineStore } from 'pinia';

export interface LayoutConfig {
  menuMode: 'overlay' | 'static';
  colorScheme: 'light' | 'dark';
  theme: string;
  scale: number;
  // Optional theme customization fields for parity with old useLayout composable
  preset?: string | null;
  primary?: string | null;
  surface?: string | null;
}

export interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  staticMenuMobileActive: boolean;
  menuHoverActive: boolean;
  profileSidebarVisible: boolean;
  configSidebarVisible: boolean;
  anchored: boolean;
  activeMenuItem: unknown | null;
}

export const useLayoutStore = defineStore('layout', () => {
  // State
  const config = ref<LayoutConfig>({
    menuMode: 'static',
    colorScheme: 'light',
    theme: 'aura-light-green',
    scale: 14,
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
  });

  const state = ref<LayoutState>({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    anchored: false,
    activeMenuItem: null,
  });

  // Getters
  const isSidebarActive = computed(
    () => state.value.overlayMenuActive || state.value.staticMenuMobileActive,
  );

  const isDarkTheme = computed(() => config.value.colorScheme === 'dark');

  // Actions
  const setScale = (scale: number) => {
    config.value.scale = scale;
  };

  const setActiveMenuItem = (_item: unknown) => {
    // Allow passing either a raw value or an object with a `value` field (for menu components)
    const item =
      typeof _item === 'object' && _item !== null
        ? ((_item as unknown as { value: unknown }).value ?? _item)
        : _item;
    state.value.activeMenuItem = item ?? null;
  };

  const onMenuToggle = () => {
    if (config.value.menuMode === 'overlay') {
      state.value.overlayMenuActive = !state.value.overlayMenuActive;
    }

    if (window.innerWidth > 991) {
      state.value.staticMenuDesktopInactive =
        !state.value.staticMenuDesktopInactive;
    } else {
      state.value.staticMenuMobileActive = !state.value.staticMenuMobileActive;
    }
  };

  const resetMenu = () => {
    state.value.overlayMenuActive = false;
    state.value.staticMenuMobileActive = false;
    state.value.menuHoverActive = false;
  };

  const setOverlayMenuActive = (active: boolean) => {
    state.value.overlayMenuActive = active;
  };

  const setStaticMenuMobileActive = (active: boolean) => {
    state.value.staticMenuMobileActive = active;
  };

  const setMenuHoverActive = (active: boolean) => {
    state.value.menuHoverActive = active;
  };

  const setColorScheme = (colorScheme: 'light' | 'dark') => {
    config.value.colorScheme = colorScheme;
  };

  const toggleDarkMode = () => {
    // Execute the DOM class toggle and config switch in a single function so we can use View Transitions API when available
    const executeDarkModeToggle = () => {
      const newScheme = config.value.colorScheme === 'dark' ? 'light' : 'dark';
      config.value.colorScheme = newScheme;
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle(
          'app-dark',
          newScheme === 'dark',
        );
      }
    };

    if (typeof document === 'undefined') {
      executeDarkModeToggle();
      return;
    }

    // Use View Transitions API if supported for a smoother theme switch
    if (!('startViewTransition' in document)) {
      executeDarkModeToggle();
      return;
    }
    (
      document as unknown as {
        startViewTransition: (callback: () => void) => void;
      }
    ).startViewTransition(() => executeDarkModeToggle());
  };

  // Aliases / additional getters for parity with old composable
  const getPrimary = computed(() => config.value.primary ?? null);
  const getSurface = computed(() => config.value.surface ?? null);
  const toggleMenu = onMenuToggle;

  const setTheme = (theme: string) => {
    config.value.theme = theme;
  };

  const setMenuMode = (menuMode: 'overlay' | 'static') => {
    config.value.menuMode = menuMode;
  };

  const setProfileSidebarVisible = (visible: boolean) => {
    state.value.profileSidebarVisible = visible;
  };

  const setConfigSidebarVisible = (visible: boolean) => {
    state.value.configSidebarVisible = visible;
  };

  const setAnchored = (anchored: boolean) => {
    state.value.anchored = anchored;
  };

  return {
    // State
    config: readonly(config),
    state: readonly(state),

    // Getters
    isSidebarActive,
    isDarkTheme,

    // Actions
    setScale,
    setActiveMenuItem,
    onMenuToggle,
    resetMenu,
    setOverlayMenuActive,
    setStaticMenuMobileActive,
    setMenuHoverActive,
    setColorScheme,
    setTheme,
    setMenuMode,
    setProfileSidebarVisible,
    setConfigSidebarVisible,
    setAnchored,
    toggleDarkMode,
    // Parity getters/aliases
    getPrimary,
    getSurface,
    toggleMenu,
  };
});
